import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { MarketAnalysisService } from '../../service/market-analysis.service';
import { MarketAnalysisInternalService } from '../../service/marketAnalysisInternalService';
import {
  MaSections, MaSelectedCharts,
  MaSelectedItems,
  MaShowItems,
} from 'src/app/domain/recovery/component/recovery-market-analysis/pojo/ma-classes';
import { LocalDate } from '../../../../core/pojo/date-variable';
import { DateService } from '../../../../util/common/date-service/date.service';
import {
  MaByIndustryChartsOptions,
  MaByIndustryDTO,
  MaRaisedByIndustryPreviousYears,
  RAISED_BY_INDUSTRY_TITLE_FIRST_PART,
} from '../../dto/MaByIndustryChartDTO';
import { appearAnimations } from '../../../../util/animations/appear-animations';
import { skeletonAnimations } from '../../../../util/animations/skeleton-animations';
import { MaChartService } from '../../service/ma-charts.service';

@Component({
  selector: 'app-market-analysis-by-industry',
  templateUrl: './market-analysis-by-industry.component.html',
  styleUrls: ['./market-analysis-by-industry.component.scss'],
  animations: [appearAnimations, skeletonAnimations],
})
export class MarketAnalysisByIndustryComponent implements OnInit, OnDestroy {
  _destroyStream: Subject<void> = new Subject<void>();
  periods: LocalDate[];
  selected = new MaSelectedItems();
  show = new MaShowItems();
  chartsOptions = new MaByIndustryChartsOptions();
  previousYears = new MaRaisedByIndustryPreviousYears();
  RAISED_BY_INDUSTRY_TITLE_FIRST_PART = RAISED_BY_INDUSTRY_TITLE_FIRST_PART;
  byIndustryData: MaByIndustryDTO;
  MaSections = MaSections;
  MaSelectedCharts = MaSelectedCharts;

  constructor(
    private marketAnalysisService: MarketAnalysisService,
    private marketInternalService: MarketAnalysisInternalService,
  ) {
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  ngOnInit(): void {
    this.show.loadingState = true;
    this.marketInternalService.selectedPeriodForMarketAnalysisState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((selectedPeriod) => {
        if (!selectedPeriod) {
          this.getReportedPeriods();
          return;
        }
        this.selected.period = selectedPeriod;
        this.setPreviousTwoYears();
        this.getByIndustryData();
      });
  }

  selectChart(section: MaSections, show: MaSelectedCharts): void {
    switch (section) {
      case MaSections.MA_BY_IND_TOP_FIVE:
        return this.handleTopFiveCharts(show);
    }
  }

  handleTopFiveCharts(show: MaSelectedCharts): void {
    switch (show) {
      case MaSelectedCharts.MA_BY_IND_TOP_FIVE_CAPITAL_RAISED:
        this.show.topFiveSection.capitalRaised = true;
        this.show.topFiveSection.numberOfDeals = false;
        break;

      case MaSelectedCharts.MA_BY_IND_TOP_FIVE_NUM_DEALS:
        this.show.topFiveSection.capitalRaised = false;
        this.show.topFiveSection.numberOfDeals = true;
        break;
    }
  }

  /**
   * Get "Dilution" table and chart data and "Total raised, per industry" charts data
   */
  private async getByIndustryData(): Promise<void> {
    this.show.loadingState = true;
    this.byIndustryData = await firstValueFrom(this.marketAnalysisService
      .getMarketAnalysisByIndustryData(this.selected.period)
      .pipe(takeUntil(this._destroyStream)),
    );
    this.chartsOptions = MaChartService.createByIndustryChartOptions(
      this.byIndustryData.raisedPerIndustry,
      this.byIndustryData.dilution.chart,
      this.byIndustryData.numberOfDeals,
      this.byIndustryData.numberOfDealsPerIndustry,
      this.byIndustryData.topFiveIndustries,
    );
    this.show.loadingState = false;
  }

  /**
   * Periods in hor-nav-bar, which will be set by admin in recovery.
   * If selected period is not set in the marketInternalService, we will set it here.
   */
  private async getReportedPeriods(): Promise<void> {
    this.periods = await firstValueFrom(this.marketAnalysisService.getListOfReportedPeriods().pipe(takeUntil(this._destroyStream)));
    if (!this.selected.period) {
      this.marketInternalService.sendReportedPeriods(this.periods);
    }
  }

  /**
   * Used in Raised by Industry charts titles.
   *
   * Example: this.selected.period = '2022-03-10'
   *
   *          this.previousYears.year1 = '2021-03-10'
   *          this.previousYears.year2 = '2020-03-10'  // Artem  decided not to use it
   */
  private setPreviousTwoYears(): void {
    this.previousYears.year1 = DateService.getPreviousYear(this.selected.period, 1);
    this.previousYears.year2 = DateService.getPreviousYear(this.selected.period, 2);
  }
}
